import React, { createContext, useContext, useState, useEffect } from "react";

interface ClientCodeContextType {
  clientCode: string;
  setClientCode: (code: string) => void;
  syncWithLocalStorage: () => void;
}

const ClientCodeContext = createContext<ClientCodeContextType | undefined>(undefined);

export const ClientCodeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [clientCode, setClientCodeState] = useState(() => {
    const stored = localStorage.getItem("clientCode");
    return stored || "admin";
  });

  useEffect(() => {
    localStorage.setItem("clientCode", clientCode);
  }, [clientCode]);

  // Sync with localStorage changes (e.g., after login)
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("clientCode");
      if (stored && stored !== clientCode) {
        setClientCodeState(stored);
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check localStorage periodically for changes
    const interval = setInterval(() => {
      const stored = localStorage.getItem("clientCode");
      if (stored && stored !== clientCode) {
        setClientCodeState(stored);
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [clientCode]);

  const setClientCode = (code: string) => {
    setClientCodeState(code);
  };

  // Function to manually sync with localStorage
  const syncWithLocalStorage = () => {
    const stored = localStorage.getItem("clientCode");
    if (stored && stored !== clientCode) {
      setClientCodeState(stored);
    }
  };

  return (
    <ClientCodeContext.Provider value={{ clientCode, setClientCode, syncWithLocalStorage }}>
      {children}
    </ClientCodeContext.Provider>
  );
};

export function useClientCode() {
  const context = useContext(ClientCodeContext);
  if (!context) {
    throw new Error("useClientCode must be used within a ClientCodeProvider");
  }
  return context;
} 